import { useEffect, useState } from 'react';

export const usePreloader = (initialLoading = true, minimumDisplayTime = 1000) => {
  const [preloaderVisible, setPreloaderVisible] = useState(initialLoading);
  const [isExiting, setIsExiting] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState('Initializing');
  const [preloaderStartTime] = useState(Date.now());

  useEffect(() => {
    if (!initialLoading) {
      // When loading is complete, start fade out
      const elapsed = Date.now() - preloaderStartTime;
      const remainingTime = Math.max(0, minimumDisplayTime - elapsed);
      
      const fadeTimer = setTimeout(() => {
        setIsExiting(true);
        // Remove preloader completely after fade out
        setTimeout(() => setPreloaderVisible(false), 300); // Match CSS transition time
      }, remainingTime);
      
      return () => clearTimeout(fadeTimer);
    }
    
    // Simulate loading stages and progress
    const stages = [
      { name: 'Initializing', duration: 300 },
      { name: 'Connecting to server', duration: 400 },
      { name: 'Fetching configuration', duration: 500 },
      { name: 'Processing data', duration: 300 },
      { name: 'Finalizing', duration: 200 }
    ];
    
    let currentStage = 0;
    let progress = 0;
    
    const stageTimer = setInterval(() => {
      if (currentStage < stages.length) {
        const currentStageData = stages[currentStage];
        setLoadingStage(currentStageData.name);
        
        const progressTimer = setInterval(() => {
          progress += 100 / (currentStageData.duration / 50);
          if (progress >= 100) {
            progress = 100;
            clearInterval(progressTimer);
          }
          setLoadingProgress(Math.min(progress, 100));
        }, 50);
        
        setTimeout(() => {
          clearInterval(progressTimer);
          currentStage++;
          progress = 0;
          if (currentStage >= stages.length) {
            clearInterval(stageTimer);
            // All stages complete, start exit
            const elapsed = Date.now() - preloaderStartTime;
            const remainingTime = Math.max(0, minimumDisplayTime - elapsed);
            
            setTimeout(() => {
              setIsExiting(true);
              // Remove preloader completely after fade out
              setTimeout(() => setPreloaderVisible(false), 300);
            }, remainingTime);
          }
        }, currentStageData.duration);
      }
    }, 0);
    
    return () => {
      clearInterval(stageTimer);
    };
  }, [initialLoading, minimumDisplayTime, preloaderStartTime]);

  return {
    preloaderVisible,
    isExiting,
    loadingProgress,
    loadingStage,
    setLoadingProgress,
    setLoadingStage
  };
};